/* ============================================================
   SINGSPACE - CONSTANTES PARTAGÉES
   ============================================================ */

import {
  BookingStatus,
  InvoiceStatus,
  PaymentStatus,
  SpaceWorkflow,
  TransactionStatus,
  UserRole,
} from '../types/index.js';

// ============================================================
// EXPORTS - MAPPING DES CHAMPS
// ============================================================

export {
  FORM_FIELD_MAPPING,
  getAllHardcodedFields,
  getCriticalFields,
  getFieldKey,
  getFieldMapping,
  getFieldsWithoutDynamicSection,
  getMappingStats,
  getSectionKey,
  isFieldMapped,
  mapFormDataToSections,
  mapFormDataToSpecificDetails,
} from './formFieldMapping.js';

// ============================================================
// LABELS ET COULEURS - STATUTS DE RÉSERVATION
// ============================================================

export const BOOKING_STATUS_CONFIG: Record<BookingStatus, { label: string; color: string; bgColor: string }> = {
  [BookingStatus.PENDING]: {
    label: 'En attente',
    color: '#856404',
    bgColor: '#fff3cd',
  },
  [BookingStatus.PAYMENT_PENDING]: {
    label: 'Paiement en attente',
    color: '#856404',
    bgColor: '#fff3cd',
  },
  [BookingStatus.PAID]: {
    label: 'Payé',
    color: '#155724',
    bgColor: '#d4edda',
  },
  [BookingStatus.CONFIRMED]: {
    label: 'Confirmé',
    color: '#155724',
    bgColor: '#d4edda',
  },
  [BookingStatus.QUOTE_REQUESTED]: {
    label: 'Devis demandé',
    color: '#0c5460',
    bgColor: '#d1ecf1',
  },
  [BookingStatus.QUOTED]: {
    label: 'Devis envoyé',
    color: '#0c5460',
    bgColor: '#d1ecf1',
  },
  [BookingStatus.APPROVED]: {
    label: 'Approuvé',
    color: '#155724',
    bgColor: '#d4edda',
  },
  [BookingStatus.REJECTED]: {
    label: 'Rejeté',
    color: '#721c24',
    bgColor: '#f8d7da',
  },
  [BookingStatus.CANCELLED]: {
    label: 'Annulé',
    color: '#666666',
    bgColor: '#e0e0e0',
  },
  [BookingStatus.COMPLETED]: {
    label: 'Terminé',
    color: '#00758d',
    bgColor: '#d1ecf1',
  },
  [BookingStatus.URGENT]: {
    label: 'URGENT',
    color: '#ffffff',
    bgColor: '#dc3545',
  },
  [BookingStatus.OVERDUE]: {
    label: 'EN RETARD',
    color: '#ffffff',
    bgColor: '#8E0B56', // SING MAGENTA
  },
};

// ============================================================
// LABELS - STATUTS DE TRANSACTION
// ============================================================

export const TRANSACTION_STATUS_CONFIG: Record<TransactionStatus, { label: string; color: string; bgColor: string }> = {
  [TransactionStatus.INITIATED]: {
    label: 'Initiée',
    color: '#666666',
    bgColor: '#e0e0e0',
  },
  [TransactionStatus.PENDING]: {
    label: 'En attente',
    color: '#856404',
    bgColor: '#fff3cd',
  },
  [TransactionStatus.PROCESSING]: {
    label: 'En cours',
    color: '#0c5460',
    bgColor: '#d1ecf1',
  },
  [TransactionStatus.COMPLETED]: {
    label: 'Complétée',
    color: '#155724',
    bgColor: '#d4edda',
  },
  [TransactionStatus.FAILED]: {
    label: 'Échouée',
    color: '#721c24',
    bgColor: '#f8d7da',
  },
  [TransactionStatus.TIMEOUT]: {
    label: 'Expirée',
    color: '#721c24',
    bgColor: '#f8d7da',
  },
  [TransactionStatus.CANCELLED]: {
    label: 'Annulée',
    color: '#666666',
    bgColor: '#e0e0e0',
  },
};

// ============================================================
// LABELS - STATUTS DE FACTURE
// ============================================================

export const INVOICE_STATUS_CONFIG: Record<InvoiceStatus, { label: string; color: string; bgColor: string }> = {
  [InvoiceStatus.PENDING]: {
    label: 'En attente',
    color: '#856404',
    bgColor: '#fff3cd',
  },
  [InvoiceStatus.PAID]: {
    label: 'Payée',
    color: '#155724',
    bgColor: '#d4edda',
  },
  [InvoiceStatus.CANCELLED]: {
    label: 'Annulée',
    color: '#666666',
    bgColor: '#e0e0e0',
  },
};

// ============================================================
// LABELS - STATUTS DE PAIEMENT
// ============================================================

export const PAYMENT_STATUS_CONFIG: Record<PaymentStatus, { label: string; color: string; bgColor: string }> = {
  [PaymentStatus.PENDING]: {
    label: 'En attente',
    color: '#856404',
    bgColor: '#fff3cd',
  },
  [PaymentStatus.SUCCEEDED]: {
    label: 'Réussi',
    color: '#155724',
    bgColor: '#d4edda',
  },
  [PaymentStatus.FAILED]: {
    label: 'Échoué',
    color: '#721c24',
    bgColor: '#f8d7da',
  },
};

// ============================================================
// LABELS - RÔLES UTILISATEUR
// ============================================================

export const USER_ROLE_CONFIG: Record<UserRole, { label: string; description: string }> = {
  [UserRole.SUPER_ADMIN]: {
    label: 'Super Admin',
    description: 'Accès complet à toutes les fonctionnalités',
  },
  [UserRole.ADMIN]: {
    label: 'Administrateur',
    description: 'Gestion des réservations et utilisateurs',
  },
  [UserRole.MANAGER]: {
    label: 'Manager',
    description: 'Gestion des réservations uniquement',
  },
};

// ============================================================
// LABELS - WORKFLOWS
// ============================================================

export const WORKFLOW_CONFIG: Record<SpaceWorkflow, { label: string; description: string; color: string }> = {
  [SpaceWorkflow.IMMEDIATE]: {
    label: 'Réservation immédiate',
    description: 'Paiement direct, confirmation automatique',
    color: '#28a745',
  },
  [SpaceWorkflow.QUOTE_REQUIRED]: {
    label: 'Devis requis',
    description: 'Demande de devis puis validation',
    color: '#00758d',
  },
  [SpaceWorkflow.APPLICATION_REQUIRED]: {
    label: 'Candidature requise',
    description: 'Dossier de candidature à soumettre',
    color: '#a4006c',
  },
};

// ============================================================
// COULEURS SING
// ============================================================

export const SING_COLORS = {
  magenta: '#a4006c',
  yellow: '#dfc32f',
  brown: '#5c4738',
  turquoise: '#00758d',
  blue: '#003d4c',
  black: '#1a1a1a',
  grayLight: '#f5f5f5',
  gray: '#e0e0e0',
  white: '#ffffff',
} as const;

// ============================================================
// DEVISE
// ============================================================

export const DEFAULT_CURRENCY = 'XAF';
export const CURRENCY_SYMBOL = 'FCFA';

// ============================================================
// FORMATS DE DATE
// ============================================================

export const DATE_FORMATS = {
  short: 'dd/MM/yyyy',
  long: 'dd MMMM yyyy',
  datetime: 'dd/MM/yyyy HH:mm',
  time: 'HH:mm',
  iso: "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
} as const;

// ============================================================
// VALIDATION
// ============================================================

export const VALIDATION = {
  phone: {
    gabonPattern: /^(\+241|00241)?[0-9]{8}$/,
    gabonMessage: 'Numéro de téléphone gabonais invalide',
  },
  email: {
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    message: 'Adresse email invalide',
  },
  password: {
    minLength: 8,
    pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
    message: 'Le mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule et un chiffre',
  },
} as const;

// ============================================================
// PAGINATION
// ============================================================

export const PAGINATION = {
  defaultPageSize: 10,
  pageSizeOptions: [10, 25, 50, 100],
} as const;

// ============================================================
// UPLOAD
// ============================================================

export const UPLOAD = {
  maxFileSize: 5 * 1024 * 1024, // 5MB
  allowedImageTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  allowedDocumentTypes: ['application/pdf', 'image/jpeg', 'image/png'],
} as const;
